import type { ItemFieldRow } from './item-field-reader';
import type { SkillTierCatalogEntry } from '../types';
import type { AbilityConditionDependencies } from './ability-conditions';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { compileCreatureSkillTier } from './creature-skill-tier';
import { storeCreatureSkillEntry } from './creature-skill-entry-storage';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { compileCreatureMetaDefaults } from './creature-meta-defaults';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import { createFromBasePlayerContext, fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerInput, FromBasePlayerMetadata, FromBaseRow } from './from-base-context';
import { constructPlayerPrefixFromBase } from './from-base-player-prefix';
import { stampNpcSkillSignatures } from './from-base-npc-signatures';
import { foldSkillTierGrants } from './skill-tier-grant-fold';

export interface FromBasePlayerSkillMetadata extends FromBasePlayerMetadata {
    skills: readonly { item_id: string; item_name: string; variable_name: string; item_type: string }[];
    skillTiers: readonly SkillTierCatalogEntry[];
    equipmentSlots: AbilityConditionDependencies['equipmentSlots'];
    vitalityStatusIds: readonly string[];
}

/** Shared tier decision for the two native skill passes. The default entrypoint
 * accepts only the raw prefix; fullConditions selects strict post-equipment
 * eligibility over a locally constructed host, without resetting its resources. */
export function constructPlayerTierSkillsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerSkillMetadata, prefix: FromBaseRow,
    fullConditions?: ReturnType<typeof createAbilityConditionEvaluator>): FromBaseRow {
    const context = createFromBasePlayerContext(base, metadata), { readers, definition } = context;
    const prior = fromBaseRecord(prefix.computed) && fromBaseRecord(prefix.computed.creature) ? prefix.computed.creature : {};
    if (!fullConditions && (prior.skills != null || prior.skill_tier_grants != null || prefix.skills != null))
        throw new TypeError('from-base-prior-skills-input');
    const host = fromBaseClone(prefix), entries = (row: ItemFieldRow, key: string) => Object.values(readers.layerEntries(row, key));
    // The PC finalizer does not apply definition-authored enchantments here.
    // Arcane/AP defaults follow preliminary essences and precede tiers-only skills.
    const ec = fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : {};
    const computedRoot = (host.computed ??= {}) as FromBaseRow;
    const creatureRoot = (computedRoot.creature ??= {}) as FromBaseRow;
    if (!fullConditions) {
        const meta = compileCreatureMetaDefaults({ directCr: null, totalLevel: null,
            hasEncounterCreatureId: phpJsonTruthy(readers.layerValue(ec, 'encounter_creature_id')),
            apsTotal: readers.layerValue(ec, 'aps_total') == null ? null : phpJsonInt(readers.layerValue(ec, 'aps_total')) });
        creatureRoot.spellcasting = { ...(fromBaseRecord(creatureRoot.spellcasting) ? creatureRoot.spellcasting : {}), arcane_threshold: meta.arcaneThreshold };
        if (meta.apsTotal !== null && meta.apsTotal !== undefined) ec.aps_total = meta.apsTotal;
    }
    const conditions = fullConditions ?? createAbilityConditionEvaluator({ ...context.dependencies, equipmentSlots: metadata.equipmentSlots,
        vitalityStatusIds: metadata.vitalityStatusIds, skillEntries: metadata.skills,
        conditionSource: kind => { throw new FromBaseStageMissing('tiers-only-condition-context', kind); } });
    const tierSets = new Map(metadata.skillTiers.map(tier => [tier.variable_name,
        new Set(entries(host, tier.variable_name).map(readLayerListEntryUuid))]));
    const fold = foldSkillTierGrants(host, { tiers: metadata.skillTiers, entries, definition,
        eligible: ability => {
            if (!fullConditions && conditions.hasRuntimeConditions(ability)) {
                // A condition inherited from a carrier needs its producer too.
                const equipmentKeys = ['requires_equipped_item', 'draw_sheathed_weapon_for_attack_ability',
                    ...['armor_type', 'weapon_classification', 'weapon_type', 'weapon_has_property'].flatMap(type =>
                        [`if_${type}_equipped_active`, `if_${type}_equipped_not_active`])];
                if (equipmentKeys.some(key => entries(ability, key).length))
                    throw new FromBaseStageMissing('inventory-construction-grants', 'skill-grant-condition');
                if (['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active'].some(key =>
                    entries(ability, key).some(entry => metadata.vitalityStatusIds.includes(readLayerListEntryUuid(entry)))))
                    throw new FromBaseStageMissing('size-shapeshift-vitality', 'skill-grant-condition');
            }
            return conditions.check(ability, host, false, !fullConditions);
        } });
    for (const [id, tier] of fold.best) tierSets.get(tier.key)!.add(id);
    if (fold.best.size) creatureRoot.skill_tier_grants = fold.grants;
    else delete creatureRoot.skill_tier_grants;
    const cache = (computedRoot._cache ??= {}) as FromBaseRow;
    cache.skill_tier_grants_sig = { uuids: fold.uuids,
        mode: Object.keys(fromBaseRecord(creatureRoot.skills) ? creatureRoot.skills : {}).length ? 's' : 'd',
        mode_sensitive: fold.modeSensitive };
    const creature = creatureRoot;
    const skills: FromBaseRow = {};
    const pc = base.player_characters;
    const payloadRows = Object.values(readCreaturePayloadStatuses(host, readers.layerValue, definition));
    const forced = (id: string): number | null => {
        const has = (row: ItemFieldRow, key: string) => entries(row, key).some(cell => readLayerListEntryUuid(cell).toUpperCase() === id.toUpperCase());
        const rows: ItemFieldRow[] = [host];
        for (const status of payloadRows) {
            const row = fromBaseRecord(status) && fromBaseRecord(status.status_arr) ? status.status_arr : null;
            if (row) rows.push(row);
        }
        if (rows.some(row => has(row, 'skill_is_rolled_at_max'))) return 20;
        return rows.some(row => has(row, 'skill_is_rolled_at_min')) ? 1 : null;
    };
    for (const skill of metadata.skills) {
        if (!fromBaseUuid(skill.item_id)) throw new FromBaseInputMissing('skill-metadata', skill.variable_name);
        const hardcodedLevel = Math.max(0, ...metadata.skillTiers.filter(tier => tierSets.get(tier.variable_name)!.has(skill.item_id)).map(tier => tier.skill_tier_level));
        const tier = compileCreatureSkillTier({ skill, hardcodedLevel, tierCatalog: metadata.skillTiers,
            skillPoints: { allocated: phpJsonInt(readers.layerValue(pc, `sp_a_${skill.variable_name}`)),
                training: phpJsonInt(readers.layerValue(pc, `sp_t_${skill.variable_name}`)),
                ascension: phpJsonInt(readers.layerValue(pc, `sp_as_${skill.variable_name}`)) } });
        const stored = storeCreatureSkillEntry({ assignedLevel: tier.tier_level,
            // The tier leaf includes the default; the storage leaf owns that
            // same default and its elision gate when passed a zero tier.
            sources: tier.tier_level === 0 ? tier.sources.filter(source => source !== 'default_flawed') : tier.sources,
            rawSp: tier.raw_sp === null ? null : { ...tier.raw_sp },
            itemType: skill.item_type, advDis: 0, reversibleBreakdown: false, advDisLines: [], globalMod: 0, perSkillMod: 0,
            perSkillModLines: [], forcedNaturalMainRoll: forced(skill.item_id) });
        if (stored) skills[skill.variable_name] = stored;
    }
    if (metadata.skills.some(skill => {
        const stored = skills[skill.variable_name];
        return skill.item_type === 'crafting_skill' && fromBaseRecord(stored) && Number(stored.tier_level) > 0;
    }))
        skills._meta = { has_crafting: true };
    creature.skills = skills;
    if (!fullConditions) stampNpcSkillSignatures(base as unknown as FromBaseRow,
        metadata as import('./from-base-player-resources').FromBasePlayerResourceMetadata, host, undefined, true);
    return host;
}

/** Unwired continuation entrypoint; a successful prefix remains an intermediate
 * result with its exact missing-stage ledger, never a completed creature. */
export function constructPlayerThroughTiersFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerSkillMetadata) {
    if (!Array.isArray(metadata.classificationSizeTypes))
        return { kind: 'missing-input' as const, stage: 'definition-classification-size-defaults', dependency: 'classification-size-metadata', id: base.entity_id };
    const prefix = constructPlayerPrefixFromBase(base, metadata);
    if (prefix.kind !== 'constructed-prefix') return prefix;
    try {
        return { ...prefix, row: constructPlayerTierSkillsFromBase(base, metadata, prefix.row),
            completedStages: [...prefix.completedStages, 'definition-classification-size-defaults', 'pre-tiers-resource-defaults', 'tiers-only-skills'],
            missingStages: prefix.missingStages.filter(stage => stage !== 'tiers-only-skills') };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const, stage: 'tiers-only-skills', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        throw error;
    }
}
